<panel width="800">
	<menu always-visible>
		<menuitem *requiredCredential="credentialType.CreateCreditMemo" (click)="createCreditMemo()" [disabled]="totalValue <= 0 || !isInvoice">{{ 'invoice.create-credit-memo' | translate }}</menuitem>
		<menuitem *requiredCredential="credentialType.CreateInvoiceObjection" (click)="createEditInvoiceObjection()" [disabled]="!ledgerEntry.open || (objection && objection.userId !== currentUserId)">{{ (objection ? (objection.userId === currentUserId ? 'invoice.edit-objection' : 'invoice.objection-exists') : 'invoice.create-objection') | translate }}</menuitem>
		<menuitem *requiredCredential="credentialType.EditInvoiceDueDate" (click)="editInvoiceDueDate()" [disabled]="!validForDueDateUpdate">{{ 'invoice.edit-invoice-duedate' | translate }}</menuitem>
		<menuitem *requiredCredential="credentialType.EditInvoiceOnHoldStatus" (click)="toggleOnHold()" [disabled]="!ledgerEntry.open || ledgerEntry.documentType !== bcDocumentType.Invoice">{{ (ledgerHeader?.onHoldReason ? 'invoice.remove-on-hold' : 'invoice.set-on-hold') | translate }}</menuitem>
	</menu>

	@if (ledgerEntry) {
		<h1>{{ 'ledger-entry-details.' + bcDocumentType[ledgerEntry.documentType] + '.header' | translate }}: {{ ledgerEntry.documentNo }}</h1>
		<h2>{{ 'ledger-entry-details.invoice.customer-no' | translate:{ customerNo: ledgerEntry.customerNo } }}</h2>
	}

	@if (loading) {
		<skeletons [defaultSkeletons]="3" />
	}
	@else {
		@if (issuedReminder || ledgerHeader?.onHoldReason) {
			<list-view [selectable]="false">
				@if (issuedReminder) {
					<list-item [attr.warning]="ledgerEntry.open" statusIcon="exclamation-triangle">{{ 'ledger-entry-details.issued-reminder' | translate: { reminderLevel: issuedReminder.reminderLevel } }}</list-item>
				}
				@if (ledgerHeader?.onHoldReason) {
					<list-item [attr.warning]="ledgerEntry.open" statusIcon="exclamation-triangle">{{ 'ledger-entry-details.on-hold.' + onHoldReason(ledgerHeader?.onHoldReason) | translate }}</list-item>
				}
			</list-view>
		}
		<scrollable>
			@if (installmentPayments || objection) {
				<section>
					@if (installmentPayments) {
						<card>
							<section header>
								<icon type="layer-group" />
								<div>
									<h3>{{ 'ledger-entry-details.payment-agreement-active' | translate }}</h3>
									{{ 'company-invoices.installment-payments.number-of-installments' | translate:{ installments: installmentPayments.length } }}
								</div>
							</section>
							<section>
								<summary [label]="'ledger-entry-details.amount-paid' | translate">{{ ((ledgerEntry.amountInclVat ?? 0) - (ledgerEntry.remainingAmountInclVat ?? 0)) | currency }}</summary>
								<summary [label]="'ledger-entry-details.ledger-remaining-value' | translate">{{ ledgerEntry.remainingAmountInclVat | currency }}</summary>
							</section>
						</card>
					}
					@if (objection) {
						<card>
							<section header>
								<icon type="section" />
								<div>
									<h3>{{ 'ledger-entry-details.objection.header' | translate }} - {{ objectionReasons.get(objection.reasonId!)?.name ?? ('reason.unknown' | translate) }}</h3>
									{{ 'ledger-entry-details.objection.registered-date' | translate: { 'date': objection.registeredDate | customDate: 'shortDateText' } }}
								</div>
							</section>
							<section one-column>
								<h3>{{ 'ledger-entry-details.objection.comment-header' | translate }}</h3>
								<p>{{ objection.comment }}</p>
								<div class="out-of-focus text-small">{{ 'ledger-entry-details.objection.created' | translate: { 'date': objection.created | customDate: 'shortDateText', 'createdBy': objection.userId | user: 'name' } }}</div>
							</section>
						</card>
					}
				</section>
			}
			@if (lines) {
				<list-view scrollable [selectable]="false">
					<header>{{ 'ledger-entry-details.column.product-dimension' | translate }}</header>
					<header>{{ 'ledger-entry-details.column.description' | translate }}</header>
					<header align-right>{{ 'ledger-entry-details.column.value' | translate }}</header>
					<row *listData="let line from lines!" [clickable]="false">
						<column>{{ line.dimensionCode1Product }}</column>
						<column>{{ line.description }}</column>
						<column align-right>{{ (!line.quantity && !line.totalNetAmount) ? '' : (line.totalNetAmount | currency) }}</column>
					</row>
				</list-view>
				<section>
					<div>{{ 'ledger-entry-details.total-excl-vat' | translate }}</div>
					<div class="align-right">{{ totalValue | currency }}</div>
					<div>{{ 'ledger-entry-details.vat' | translate }}</div>
					<div class="align-right">{{ vatValue | currency }}</div>
					<div><strong>{{ 'ledger-entry-details.total-incl-vat' | translate }}</strong></div>
					<div class="align-right"><strong>{{ totalValueInclVat | currency }}</strong></div>
				</section>
			}
			@else {
				<section one-column>
					<div>{{ 'ledger-entry-details.no-bc-document-found' | translate }}</div>
				</section>
			}
		</scrollable>
	}
	<section>
		<tooltip *requiredCredential="credentialType.AccountingsystemSendDocuments" [text]="'ledger-entry-details.send-document.unavailable-document' | translate" [disabled]="documentAvailableForResend">
			<button [active]="documentAvailableForResend" *requiredCredential="credentialType.AccountingsystemSendDocuments" [ignoreValidation]="true" (click)="sendDocument()" #sendButton>
				<icon type="paper-plane" />{{ (!documentId ? 'ledger-entry-details.send-document-using-farpay' : 'ledger-entry-details.send-document') | translate }}
			</button>
		</tooltip>
		<tooltip [text]="'ledger-entry-details.download-document.unavailable-document' | translate" [disabled]="documentAvailableForDownload">
			<button [active]="documentAvailableForDownload" [ignoreValidation]="true" (click)="downloadDocument()" [loader]="false">
				<icon type="download" />{{ 'ledger-entry-details.download-document' | translate }}
			</button>
		</tooltip>
	</section>
	@if (ledgerEntry && ledgerEntry.documentType == bcDocumentType.Invoice && ledgerEntry.open && ledgerEntry.remainingAmountInclVat) {
		<section *requiredCredential="credentialType.EditInvoiceInstallmentPayments">
			<button (click)="editInstallmentPayments()" [loader]="false" [active]="canEditInstallmentPayments"><icon type="layer-group" />{{ 'invoice.edit-invoice-payments' | translate }}</button>
		</section>
	}
</panel>
